import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import type { DialogKey, DialogProps } from '~/global-state/dialog';
import { dialogKeysAtom, useDialogHandlers } from '~/global-state/dialog';

interface UseDialogOptions {
  /**
   * Register the dialog on its first open instead of on mount, for callers that rarely open it, so it isn't
   * mounted alongside them for nothing.
   */
  registerOnOpen?: boolean;
}

export function useDialog<K extends DialogKey>(id: K, { registerOnOpen = false }: UseDialogOptions = {}) {
  const { handleCloseDialog, handleOpenDialog: openDialog } = useDialogHandlers(id);
  const setDialogKeys = useSetAtom(dialogKeysAtom);
  const isRegisteredRef = useRef(false);

  const register = useCallback(() => {
    isRegisteredRef.current = true;
    setDialogKeys((keys) => (keys.includes(id) ? keys : [...keys, id]));
  }, [id, setDialogKeys]);

  useEffect(() => {
    if (!registerOnOpen) {
      register();
    }

    return () => {
      if (isRegisteredRef.current) {
        isRegisteredRef.current = false;
        setDialogKeys((keys) => keys.filter((key) => key !== id));
      }
    };
  }, [id, register, registerOnOpen, setDialogKeys]);

  const handleOpenDialog = useCallback(
    (props: DialogProps[K]) => {
      register();
      openDialog(props);
    },
    [openDialog, register],
  );

  return { handleCloseDialog, handleOpenDialog };
}
